/**
 * Обёртка над `AudioContext` §8 (1.20), design.md решение 2. Тон
 * синтезируется осциллятором, без аудиофайла: короткий сигнал на
 * срабатывание алерта, без загрузки статики. Частота, длительность и
 * громкость — именованные константы, техника показа, не порог §11.
 *
 * `createAlertSound()` при создании ничего не делает: ни слушателей жеста,
 * ни `AudioContext` (ревью stage6-alert-sound, пункт 3) — побочные эффекты
 * начинаются со `start()` и заканчиваются `dispose()`. Это нужно для
 * React StrictMode (эффект вызывается, отменяется и вызывается снова на
 * монтировании) и для того, чтобы звук жил только у вошедшего пользователя,
 * а не с момента импорта модуля.
 *
 * Если после создания контекст в состоянии `suspended` (или `interrupted`
 * на Safari — оба считаются блокировкой), браузер ждёт жеста пользователя.
 * Обёртка отдаёт признак «заблокирован» и снимает его по первому жесту на
 * переданной цели (по умолчанию — `document`) или по явному вызову
 * `unlock()`. Жест слушается широко — указатель, тачскрин и клавиатура
 * (пункт 6): `pointerdown`, `pointerup`, `touchend`, `click`, `keydown`, все
 * на фазе перехвата, чтобы не зависеть от того, что именно у цели жеста
 * останавливает всплытие.
 */

interface AlertAudioParam {
  value: number;
}

interface AlertAudioNode {
  connect(destination: AlertAudioNode): void;
}

interface AlertOscillatorNode extends AlertAudioNode {
  frequency: AlertAudioParam;
  start(when?: number): void;
  stop(when?: number): void;
}

interface AlertGainNode extends AlertAudioNode {
  gain: AlertAudioParam;
}

/** Состояния `AudioContext`, включая `interrupted` — нестандартное состояние Safari. */
export type AlertAudioContextState = "suspended" | "running" | "closed" | "interrupted";

/** Состояния, в которых звук браузером заблокирован до жеста пользователя. */
const BLOCKED_STATES: ReadonlySet<AlertAudioContextState> = new Set(["suspended", "interrupted"]);

/** Минимальная форма `AudioContext`, которой пользуется этот модуль. */
export interface AlertAudioContext {
  readonly state: AlertAudioContextState;
  readonly currentTime: number;
  readonly destination: AlertAudioNode;
  onstatechange: ((event: Event) => void) | null;
  resume(): Promise<void>;
  close(): Promise<void>;
  createOscillator(): AlertOscillatorNode;
  createGain(): AlertGainNode;
}

type AlertGestureEventType = "pointerdown" | "pointerup" | "touchend" | "click" | "keydown";

/** Все типы жеста, снимающие блокировку звука (пункт 6 ревью stage6-alert-sound). */
const GESTURE_EVENT_TYPES: readonly AlertGestureEventType[] = [
  "pointerdown",
  "pointerup",
  "touchend",
  "click",
  "keydown",
];

/** Минимальная форма цели жеста — по умолчанию `document`. */
export interface AlertGestureTarget {
  addEventListener(
    type: AlertGestureEventType,
    listener: () => void,
    options?: { capture?: boolean },
  ): void;
  removeEventListener(
    type: AlertGestureEventType,
    listener: () => void,
    options?: { capture?: boolean },
  ): void;
}

/** Частота тона алерта — один сигнал на все триггеры (design.md решение 2). */
export const ALERT_TONE_FREQUENCY_HZ = 880;

/** Длительность тона алерта, миллисекунды. */
export const ALERT_TONE_DURATION_MS = 220;

/** Громкость тона алерта, доля от максимума `GainNode`. */
const ALERT_TONE_GAIN = 0.2;

export interface CreateAlertSoundOptions {
  /** Фабрика контекста — подменяется в тестах. По умолчанию `AudioContext`. */
  createContext?: () => AlertAudioContext;
  /** Цель жеста разблокировки — подменяется в тестах. По умолчанию `document`. */
  target?: AlertGestureTarget;
}

export interface AlertSound {
  /** Вешает слушатели жеста и заводит контекст. Повторный вызов после `dispose()` работает снова. */
  start(): void;
  /** Снимает слушатели жеста, закрывает контекст (ошибку `close()` глотает) и сбрасывает его. */
  dispose(): void;
  /** Играет короткий тон. Молча ничего не делает, если контекст недоступен, заблокирован или не заведён. */
  play(): void;
  /** Заблокирован ли звук браузером до первого жеста пользователя. До `start()` — всегда `false`. */
  isBlocked(): boolean;
  /** Подписка на смену блокировки. Возвращает функцию отписки. */
  subscribe(listener: (blocked: boolean) => void): () => void;
  /** Снимает блокировку тем же путём, что и жест — для клика по пометке в шапке. */
  unlock(): void;
}

export function createAlertSound(options: CreateAlertSoundOptions = {}): AlertSound {
  const createContext = options.createContext ?? (() => new AudioContext());
  const target = options.target ?? document;

  let context: AlertAudioContext | undefined;
  let blocked = false;
  let started = false;
  const listeners = new Set<(blocked: boolean) => void>();

  function setBlocked(next: boolean): void {
    if (blocked === next) {
      return;
    }
    blocked = next;
    for (const listener of listeners) {
      listener(blocked);
    }
  }

  function onContextStateChange(): void {
    if (context) {
      setBlocked(BLOCKED_STATES.has(context.state));
    }
  }

  /** Создаёт контекст при первом обращении после `start()` и запоминает его на вкладку. */
  function ensureContext(): AlertAudioContext | undefined {
    if (!started) {
      return undefined;
    }
    if (context) {
      return context;
    }
    try {
      context = createContext();
    } catch {
      return undefined;
    }
    context.onstatechange = onContextStateChange;
    setBlocked(BLOCKED_STATES.has(context.state));
    return context;
  }

  function resumeIfBlocked(): void {
    const ctx = ensureContext();
    if (!ctx || !BLOCKED_STATES.has(ctx.state)) {
      return;
    }
    ctx
      .resume()
      .then(() => {
        // Поздний `resume()` контекста, который к этому моменту уже сменил
        // владелец — `dispose()` снял его и `start()` завёл новый — не
        // должен трогать блокировку нового контекста (ревью
        // stage6-alert-sound, followup): жест снимает блокировку только у
        // того контекста, который он реально разблокировал.
        if (ctx === context) {
          setBlocked(BLOCKED_STATES.has(ctx.state));
        }
      })
      .catch(() => {
        // Отказ браузера снять блокировку — жест не считается за отвязку;
        // следующий жест попробует снова.
      });
  }

  const onGesture = () => resumeIfBlocked();

  return {
    start() {
      if (started) {
        return;
      }
      started = true;
      for (const type of GESTURE_EVENT_TYPES) {
        target.addEventListener(type, onGesture, { capture: true });
      }
      ensureContext();
    },
    dispose() {
      if (!started) {
        return;
      }
      started = false;
      for (const type of GESTURE_EVENT_TYPES) {
        target.removeEventListener(type, onGesture, { capture: true });
      }
      if (context) {
        context.onstatechange = null;
        context.close().catch(() => {
          // Контекст уже закрыт или окружение отказало — вкладке всё равно
          // предстоит забыть о нём.
        });
      }
      context = undefined;
      setBlocked(false);
    },
    play() {
      const ctx = ensureContext();
      if (!ctx || BLOCKED_STATES.has(ctx.state)) {
        return;
      }
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.frequency.value = ALERT_TONE_FREQUENCY_HZ;
      gain.gain.value = ALERT_TONE_GAIN;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + ALERT_TONE_DURATION_MS / 1000);
    },
    isBlocked() {
      // Не зовёт `ensureContext()`: `getSnapshot()` может вызвать это во
      // время рендера, а контекст создаётся только в `start()`, `play()` и
      // `unlock()` (ревью stage6-alert-sound, followup). До `start()` флаг
      // остаётся исходным `false`.
      return started && blocked;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    unlock() {
      resumeIfBlocked();
    },
  };
}
